import * as React from "react";

import {
    ContextualActionSet,
    ContextualActionsProvider,
} from "../../Internals/FormActionHandlers/ContextualActionsContext";
import { useUserPicklistRegistry } from "../../FormParts/UserPicklistDeclaration/UserPicklist/UserPicklistRegistryContext";
import { reject } from "../../../../../Common/TypingUtils";
import { NidusEntry, NidusEntryId } from "../../../../../Common/NidusClient/INidusClient";

export interface ReferencedFieldsProps {
    referencedEntryId: undefined | NidusEntryId;
    renderReferencedEditor: React.ReactNode;
    renderManualEditor: React.ReactNode;
    userPicklistName?: string;
    onApplyReferencedValue: (referencedEntry: undefined | NidusEntry) => void;
    onRemoveReference: (referencedEntry: undefined | NidusEntry) => void;
}

export function ReferencedFields(props: ReferencedFieldsProps): JSX.Element {
    const userPicklistRegistry = useUserPicklistRegistry();

    const contextualActions = React.useMemo(
        (): ContextualActionSet => ({
            ["referencedfields.SelectFromReference"]: async () => {
                if (props.userPicklistName == undefined) {
                    throw new Error(
                        "Contextual selection from reference supports only user picklists. Use userPicklist:{name}"
                    );
                }
                const userPicklist =
                    userPicklistRegistry.getUserPicklist(props.userPicklistName) ??
                    reject(`Picklist with name ${props.userPicklistName} is not defined`);
                const nidusValue = await userPicklist.getExistingValue();
                if (nidusValue != undefined) {
                    props.onApplyReferencedValue(nidusValue);
                }
            },
            ["referencedfields.ClearFields"]: async () => {
                if (props.userPicklistName == undefined) {
                    throw new Error(
                        "Contextual selection from reference supports only user picklists. Use userPicklist:{name}"
                    );
                }
                props.onApplyReferencedValue(undefined);
            },
            ["referencedfields.SwitchToManual"]: async () => {
                if (props.userPicklistName == undefined) {
                    throw new Error(
                        "Contextual selection from reference supports only user picklists. Use userPicklist:{name}"
                    );
                }
                props.onRemoveReference(undefined);
            },
        }),
        []
    );

    let content;
    if (props.referencedEntryId == undefined || props.referencedEntryId === "") {
        content = <>{props.renderManualEditor}</>;
    } else {
        content = <>{props.renderReferencedEditor}</>;
    }

    return <ContextualActionsProvider actions={contextualActions}>{content}</ContextualActionsProvider>;
}
